/**
 * The Data step's selection → the DATASET the widget will be built on
 * (CT-5725).
 *
 * In the rev4 dataset-first flow the Data step no longer describes the
 * widget's query: it describes a **reusable record set**. So what travels to
 * `/dataset/create` is the entity, the join graph, the base filters and
 * *every column the widget reads* — the exposed ones, the groupers and the
 * columns the measures aggregate — with no `resultMode`, no `summary` and no
 * aggregation anywhere. The operations stay in the widget's bindings.
 *
 * ## One selection, one dataset
 *
 * `create` is idempotent on the selection, so the builder calls it whenever
 * the author moves on to Configure and gets the same `datasetUuid` back until
 * they change something. `selectionSignature` is how the builder knows it has
 * already asked: the same fields and filters in any order produce the same
 * string, which is the same rule the backend matches on.
 */

import { buildQuerySources, sourceKeyFor } from './querySources.js';
import { wireFilters } from './buildWidgetPayload.js';

const EMPTY = Object.freeze([]);

/** Field uuids a row of the Data step refers to, whatever shape it has. */
const fieldUuidOf = (row) => row?.fieldUuid || row?.column || '';

/**
 * Every field the widget reads, once each, in the order the author built
 * them: the exposed columns first, then the groupers, then the measures'
 * columns. A row count reads no column and contributes none.
 */
export function selectedFieldUuids({ dataset, validMeasures = EMPTY }) {
  const exposed = (dataset?.exposedColumns ?? EMPTY)
    .map((column) => (typeof column === 'string' ? column : column?.fieldUuid))
    .filter(Boolean);
  const grouped = (dataset?.groupers ?? EMPTY).map(fieldUuidOf).filter(Boolean);
  const measured = validMeasures.map(fieldUuidOf).filter(Boolean);
  return [...new Set([...exposed, ...grouped, ...measured])];
}

/**
 * The body `/dataset/create` takes for this selection.
 *
 * @param {object} form State from `useWidgetBuilderForm`.
 * @param {{ columns?: Array }} [options] Loaded column metadata, read for each
 *   field's origin so a multi-entity selection can declare its graph.
 * @returns {object|null} The request body, or `null` when the selection names
 *   no entity or no field — there is no dataset to ask for.
 */
export function buildDatasetRequest(form, { columns = EMPTY } = {}) {
  const { dataset, validFilters = EMPTY, validMeasures = EMPTY } = form;
  const compositeEntityUuid = dataset?.compositeEntityUuid || '';
  const fieldUuids = selectedFieldUuids({ dataset, validMeasures });
  if (!compositeEntityUuid || !fieldUuids.length) return null;

  const originOf = (fieldUuid) =>
    columns.find((column) => column.fieldUuid === fieldUuid)?.origin;

  const { sources, keyByEntityUuid } = buildQuerySources({
    rootEntityUuid: dataset.entityRef || dataset.sourceId || '',
    origins: [
      ...fieldUuids,
      ...validFilters.map((filter) => filter.fieldUuid || filter.column),
    ].filter(Boolean).map(originOf),
  });
  const keyOf = (fieldUuid) => sourceKeyFor(originOf(fieldUuid), keyByEntityUuid);

  return {
    compositeEntityUuid,
    // Omitted for a single-entity selection, the same rule the widget payload
    // follows: `buildQuerySources` answers `null` when every field is the
    // root's.
    sources: sources ?? [],
    columns: fieldUuids.map((fieldUuid, index) => ({
      fieldUuid,
      ...(keyOf(fieldUuid) ? { sourceKey: keyOf(fieldUuid) } : {}),
      displayOrder: index + 1,
    })),
    filters: validFilters.flatMap(wireFilters).map((filter) => ({
      ...filter,
      ...(keyOf(filter.fieldUuid) ? { sourceKey: keyOf(filter.fieldUuid) } : {}),
    })),
    ...(form.name?.trim() ? { name: form.name.trim() } : {}),
  };
}

/**
 * A stable string for a selection, so the builder can tell whether the
 * dataset it holds still describes what the Data step says.
 *
 * Order-insensitive on purpose: the backend matches a selection the same way,
 * and re-asking for a dataset the author already has would be a request per
 * keystroke. The widget's **name** is left out — it is display text, and
 * changing it must not make a second dataset.
 */
export function selectionSignature(request) {
  if (!request) return '';
  const columns = request.columns
    .map((column) => `${column.fieldUuid}:${column.sourceKey ?? ''}`)
    .sort();
  const filters = request.filters
    .map((filter) => [
      filter.fieldUuid,
      filter.operator,
      JSON.stringify(filter.value ?? null),
      filter.sourceKey ?? '',
    ].join('|'))
    .sort();
  const sources = (request.sources ?? [])
    .map((source) => `${source.key}:${source.entityUuid}:${source.role}`)
    .sort();
  return JSON.stringify({
    entity: request.compositeEntityUuid, columns, filters, sources,
  });
}

export default buildDatasetRequest;
